import { Box, useMediaQuery } from '@mui/material';
import { useTranslation } from '@chr/online-shared-ui-localizations';

import { useAnalytics } from '@/common/hooks/useAnalytics';
import usePrintStore from '@/common/stores/print-store';
import useTabStore from '@/common/stores/tab-store';
import { Tab, TabPanel, Tabs, ActiveTabContext } from '@/common/components/tabs';
import { VisibilityBooking } from '@/common/apis/dtos';

import { Tracking } from './tracking/Tracking';
import { CargoDetails } from './cargo-details/CargoDetails';
import { Documents } from './documents/Documents';
import { RelatedBookings } from './related-bookings/RelatedBookings';
import { RelatedPurchaseOrders } from './related-pos/RelatedPurchaseOrders';
import customTheme from '@/common/components/theme-provider/customTheme';
import { useGetPermissions } from '@/common/apis/permissions-query';

export interface DetailTabsProps {
  booking: VisibilityBooking;
}

export const DetailTabs = ({ booking }: DetailTabsProps) => {
  const theme = customTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));
  const translate = useTranslation();
  const analytics = useAnalytics();
  const { isPrintMode } = usePrintStore();
  const { activeTab, setActiveTab } = useTabStore();

  const handleChange = (e: React.SyntheticEvent<Element, Event>, value: string) => {
    analytics.trackEvent(value, 'tab', 'click');
    setActiveTab(value);
  };

  // In GF, we only have 1-0 or 1-1 booking to shipment mapping
  const shipmentNumbers = booking.shipmentNumber ? [booking.shipmentNumber] : [];

  const commoditySummary = booking.commoditySummary ?? {};
  const cargoSummary = booking.cargoSummary ?? {};
  const relatedPurchaseOrders = booking.relatedPurchaseOrders ?? [];
  const relatedBookings = booking.relatedBookings ?? [];
  const purchaseOrderItems = booking.purchaseOrderItems ?? [];
  const nonPurchaseOrderItems = booking.nonPurchaseOrderItems ?? [];

  const hasRelatedPos = relatedPurchaseOrders.some(po => (po?.purchaseOrderId ?? 0) > 0);
  const hasRelatedBookings = relatedBookings.some(rb => (rb?.bookingNumber));

  const customerPartyCode = booking.customerInfo?.customer?.partyCode ?? '';
  const { data: permissions } = useGetPermissions([customerPartyCode]);

  return (
    <ActiveTabContext.Provider value={activeTab}>
      <Box className="bookingsdetail-tabs">
        <Tabs
          variant={isMobileScreen ? 'fullWidth' : 'standard'}
          value={activeTab}
          onChange={handleChange}
          orientation={isMobileScreen ? 'vertical' : 'horizontal'}
          className="bookingdetail-tabs-menu no-print"
          aria-label={translate('Booking Detail')}
        >
          <Tab
            label={`${translate('Tracking')}`}
            tabsId="bookingsDetail"
            value="tracking"
          />
          <Tab
            label={`${translate('Cargo Details')}`}
            tabsId="bookingsDetail"
            value="cargoDetails"
          />

          <Tab
            label={`${translate('Related Bookings')}`}
            tabsId="bookingsDetail" value="relatedBookings"
            isVisible={hasRelatedBookings}
          />

          <Tab
            label={`${translate('Related Purchase Orders')}`}
            tabsId="bookingsDetail"
            value="relatedPOs"
            isVisible={hasRelatedPos}
          />

          <Tab
            label={`${translate('Documents')}`}
            tabsId="bookingsDetail"
            value="documents"
          />
        </Tabs>

        <TabPanel value="tracking" isPrintMode={isPrintMode} tabsId="bookingsDetail">
          <Tracking booking={booking} />
        </TabPanel>

        <TabPanel value="cargoDetails" isPrintMode={isPrintMode} tabsId="bookingsDetail">
          <CargoDetails
            commoditySummary={commoditySummary}
            cargoSummary={cargoSummary}
            nonPurchaseOrderItems={nonPurchaseOrderItems}
            purchaseOrderItems={purchaseOrderItems}
          />
        </TabPanel>

        <TabPanel value="relatedBookings" isPrintMode={isPrintMode} isVisible={hasRelatedBookings} tabsId="bookingsDetail">
          <RelatedBookings booking={booking} />
        </TabPanel>

        <TabPanel value="relatedPOs" isPrintMode={isPrintMode} isVisible={hasRelatedPos} tabsId="bookingsDetail">
          <RelatedPurchaseOrders booking={booking} />
        </TabPanel>

        <TabPanel value="documents" isPrintMode={isPrintMode} tabsId="bookingsDetail">
          <Documents bookingNumber={booking.customerBookingNumber ?? 0} loadNumbers={shipmentNumbers} permissions={permissions} customerPartyCode={customerPartyCode} />
        </TabPanel>
      </Box>
    </ActiveTabContext.Provider>
  );
};
